import type { ColumnsType } from "antd/es/table";

import { TrafficShare, ChangePercentage, RankCell } from "app/components/React/Table/cells";
import { GroupTrafficShare } from "app/components/React/Table/cells/GroupTrafficShare";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { NO_DATA_SIGN } from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { swNumberFilter } from "app/filters/ngFilters";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";

import { TrafficSourcesCell } from "./components/TrafficSourceCell/TrafficSourceCell";
import type { ITrafficSourceDataItem, QualificationData, SortConfig } from "./types";

const getSortOrder = (sortConfig: SortConfig, columnKey: string) =>
    sortConfig.key === columnKey ? (sortConfig.ascending ? "ascend" : "descend") : null;

const COLUMN_CONFIGS = {
    sourceType: {
        titleKey: "analysis.source.referrals.table.columns.sourcetype.title",
        tooltipKey: "analysis.source.referrals.table.columns.sourcetype.title.tooltip",
        width: 160,
    },
    category: {
        titleKey: "analysis.source.referrals.table.columns.cat.title",
        tooltipKey: "analysis.source.referrals.table.columns.cat.title.tooltip",
        width: 220,
    },
    change: {
        titleKey: "analysis.source.referrals.table.columns.change.title",
        tooltipKey: "analysis.source.referrals.table.columns.change.title.tooltip",
        width: 104,
    },
    rank: {
        titleKey: "analysis.source.referrals.table.columns.rank.title",
        tooltipKey: "analysis.source.referrals.table.columns.rank.title.tooltip",
        width: 120,
    },
    share: {
        titleKey: "analysis.source.referrals.table.columns.share.title",
        tooltipKey: "analysis.source.referrals.table.columns.share.title.tooltip",
        width: 130,
    },
    groupShare: {
        titleKey: "analysis.source.referrals.table.columns.grouptrafficshare.title",
        tooltipKey: "analysis.source.referrals.table.columns.grouptrafficshare.title.tooltip",
        width: 220,
    },
    siteOrigins: {
        titleKey: "analysis.source.referrals.table.columns.groupsharesplit.title",
        tooltipKey: "analysis.source.referrals.table.columns.groupsharesplit.title.tooltip",
        width: 220,
    },
} as const;

const COLUMN_KEYS = {
    index: "index",
    domain: "domain",
    sourceType: "sourceType",
    share: "share",
    category: "category",
    change: "change",
    rank: "rank",
    siteOrigins: "siteOrigins",
} as const;

const createTextColumn = (key: keyof ITrafficSourceDataItem, titleKey: string, tooltipKey: string, width: number) => ({
    title: translate(titleKey),
    tooltipTitle: translate(titleKey),
    tooltipSubtitle: translate(tooltipKey),
    dataIndex: key,
    key: COLUMN_KEYS[key as keyof typeof COLUMN_KEYS], // Use COLUMN_KEYS for consistent mapping
    width,
    sorter: true,
});

const createNumberColumn = (
    key: keyof ITrafficSourceDataItem,
    titleKey: string,
    tooltipKey: string,
    width: number,
    align: "left" | "right",
) => ({
    title: translate(titleKey),
    tooltipTitle: translate(titleKey),
    tooltipSubtitle: translate(tooltipKey),
    dataIndex: key,
    key: COLUMN_KEYS[key as keyof typeof COLUMN_KEYS],
    width,
    align,
    sorter: true,
});

const createShareColumn = (titleKey: string, tooltipKey: string, width: number) => ({
    title: translate(titleKey),
    dataIndex: "share",
    key: COLUMN_KEYS.share,
    width,
    align: "left" as const,
    sorter: true,
    render: (share: number, record: ITrafficSourceDataItem) => (
        <TrafficShare
            value={share}
            row={record}
        />
    ),
    tooltipTitle: translate(titleKey),
    tooltipSubtitle: translate(tooltipKey),
});

export const getTrafficSourcesTableColumns = (
    dataLength: number,
    sortConfig: SortConfig,
    visibleColumns?: Set<string>,
    handleOpenQualificationSideBar?: (data: QualificationData) => void,
    currentPage = 1,
): ColumnsType<ITrafficSourceDataItem> => {
    const isCompare = isCompareMode();
    const pageSize = 100;

    const baseColumns = [
        {
            // @ts-expect-error FIXME: Property 'parent' does not exist on type 'ITrafficSourceDataItem'
            ...getIndexCellPropsForTableV2<ITrafficSourceDataItem>(currentPage, pageSize, (record) => !record?.parent),
            width: 51,
        },
        {
            title: `${translate("analysis.source.referrals.table.columns.trafficsource.title")} (${swNumberFilter()(dataLength)})`,
            dataIndex: "domain",
            key: COLUMN_KEYS.domain,
            width: 220,
            sorter: true,
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.domain),
            render: (domain: string, record: ITrafficSourceDataItem) => (
                <TrafficSourcesCell
                    value={domain}
                    row={record}
                    handleOpenQualificationSideBar={handleOpenQualificationSideBar}
                />
            ),
            tooltipTitle: translate("analysis.source.referrals.table.columns.trafficsource.title"),
            tooltipSubtitle: translate("analysis.source.referrals.table.columns.trafficsource.title.tooltip"),
        },
    ];

    const singleModeColumns = [
        {
            ...createShareColumn(
                COLUMN_CONFIGS.share.titleKey,
                COLUMN_CONFIGS.share.tooltipKey,
                COLUMN_CONFIGS.share.width,
            ),
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.share),
        },
        {
            ...createNumberColumn(
                "change",
                COLUMN_CONFIGS.change.titleKey,
                COLUMN_CONFIGS.change.tooltipKey,
                COLUMN_CONFIGS.change.width,
                "left",
            ),
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.change),
            render: (change: number, record: ITrafficSourceDataItem) => (
                <ChangePercentage
                    value={change}
                    row={record}
                />
            ),
        },
        {
            ...createTextColumn(
                "sourceType",
                COLUMN_CONFIGS.sourceType.titleKey,
                COLUMN_CONFIGS.sourceType.tooltipKey,
                COLUMN_CONFIGS.sourceType.width,
            ),
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.sourceType),
        },
        {
            ...createNumberColumn(
                "rank",
                COLUMN_CONFIGS.rank.titleKey,
                COLUMN_CONFIGS.rank.tooltipKey,
                COLUMN_CONFIGS.rank.width,
                "left",
            ),
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.rank),
            render: (rank: number) => <RankCell value={rank} />,
        },
        {
            ...createTextColumn(
                "category",
                COLUMN_CONFIGS.category.titleKey,
                COLUMN_CONFIGS.category.tooltipKey,
                COLUMN_CONFIGS.category.width,
            ),
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.category),
        },
    ];

    const compareModeColumns = [
        {
            ...createTextColumn(
                "sourceType",
                COLUMN_CONFIGS.sourceType.titleKey,
                COLUMN_CONFIGS.sourceType.tooltipKey,
                COLUMN_CONFIGS.sourceType.width,
            ),
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.sourceType),
        },
        {
            title: translate(COLUMN_CONFIGS.groupShare.titleKey),
            dataIndex: "totalShare",
            key: COLUMN_KEYS.share,
            width: COLUMN_CONFIGS.groupShare.width,
            align: "left" as const,
            sorter: true,
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.share),
            render: (totalShare: number, record: ITrafficSourceDataItem) => (
                <TrafficShare
                    value={totalShare}
                    row={record}
                />
            ),
            tooltipTitle: translate(COLUMN_CONFIGS.groupShare.titleKey),
            tooltipSubtitle: translate(COLUMN_CONFIGS.groupShare.tooltipKey),
        },
        {
            title: translate(COLUMN_CONFIGS.siteOrigins.titleKey),
            dataIndex: "siteOrigins",
            key: COLUMN_KEYS.siteOrigins,
            width: COLUMN_CONFIGS.siteOrigins.width,
            render: (sitesClickShare: Record<string, number>) =>
                sitesClickShare ? <GroupTrafficShare value={sitesClickShare} /> : NO_DATA_SIGN,
            tooltipTitle: translate(COLUMN_CONFIGS.siteOrigins.titleKey),
            tooltipSubtitle: translate(COLUMN_CONFIGS.siteOrigins.tooltipKey),
        },
        {
            ...createTextColumn(
                "category",
                COLUMN_CONFIGS.category.titleKey,
                COLUMN_CONFIGS.category.tooltipKey,
                COLUMN_CONFIGS.category.width,
            ),
            sortOrder: getSortOrder(sortConfig, COLUMN_KEYS.category),
        },
    ];

    const modeSpecificColumns = isCompare ? compareModeColumns : singleModeColumns;
    const allColumns = [...baseColumns, ...modeSpecificColumns];

    return allColumns.filter((column, index) => {
        if (index < 2) {
            return true;
        } // Index and Traffic Source always visible
        return visibleColumns?.has(column.key) ?? true;
    }) as ColumnsType<ITrafficSourceDataItem>;
};

export const getTrafficSourcesTableColumnsForPicker = (
    visibleColumns?: Set<string>,
): Array<{ key: string; displayName: string; visible: boolean }> => {
    const isCompare = isCompareMode();

    // Single mode: share, change, sourceType, rank, category
    const singleModeColumns = [
        { key: COLUMN_KEYS.share, displayName: translate(COLUMN_CONFIGS.share.titleKey) },
        { key: COLUMN_KEYS.change, displayName: translate(COLUMN_CONFIGS.change.titleKey) },
        { key: COLUMN_KEYS.sourceType, displayName: translate(COLUMN_CONFIGS.sourceType.titleKey) },
        { key: COLUMN_KEYS.rank, displayName: translate(COLUMN_CONFIGS.rank.titleKey) },
        { key: COLUMN_KEYS.category, displayName: translate(COLUMN_CONFIGS.category.titleKey) },
    ];

    const compareModeColumns = [
        { key: COLUMN_KEYS.sourceType, displayName: translate(COLUMN_CONFIGS.sourceType.titleKey) },
        { key: COLUMN_KEYS.share, displayName: translate(COLUMN_CONFIGS.groupShare.titleKey) },
        { key: COLUMN_KEYS.siteOrigins, displayName: translate(COLUMN_CONFIGS.siteOrigins.titleKey) },
        { key: COLUMN_KEYS.category, displayName: translate(COLUMN_CONFIGS.category.titleKey) },
    ];

    const columnConfigs = isCompare ? compareModeColumns : singleModeColumns;

    return columnConfigs.map((config) => ({
        ...config,
        visible: visibleColumns?.has(config.key) ?? true,
    }));
};
